import React, { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import TemperatureControl from "./TemperatureControl";
import EnvironmentalDisplay from "./EnvironmentalDisplay";
import QualityGradeDisplay from "./QualityGradeDisplay";
import BrewSlots from "./brew_station/BrewSlots";
import QuantityControl from "./brew_station/QuantityControl";
import BrewButton from "./brew_station/BrewButton";
import { useGameStateContext } from "../GameStateProvider";
import { useBrewingActions } from "../hooks/useBrewingActions";
import { CONSTANTS } from "../data";
import { getAllItemsMap, getNormalizedRecipesMap } from "../utils/dataUtils";

interface BrewStationProps {
  stationIndex: number;
  constraintsRef: React.RefObject<HTMLElement>;
  gridDimensions: {
    tileSize: number;
    gridCols: number;
    gridRows: number;
    padding: number;
    totalWidth: number;
    totalHeight: number;
  };
  isLargeScreen: boolean;
}

const triggerHapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'light') => {
  if (CONSTANTS.TOUCH_INTERACTION.HAPTIC_FEEDBACK_ENABLED && 'vibrate' in navigator) {
    const patterns = {
      light: [10],
      medium: [20],
      heavy: [30]
    };
    navigator.vibrate(patterns[type]);
  }
};

const BrewStation: React.FC<BrewStationProps> = ({ stationIndex, constraintsRef, gridDimensions, isLargeScreen }) => {
  const { state, dispatch } = useGameStateContext();
  const {
    handleRemoveSlotItem,
    handleAttemptBrew,
    handleTempChange,
    handleQuantityChange,
  } = useBrewingActions();

  const station = state.brewStations[stationIndex];
  const [isDragging, setIsDragging] = useState(false);
  const [touchFeedback, setTouchFeedback] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  const { gridToPixel, pixelToGrid, clampToGrid } = React.useMemo(() => {
    const { tileSize, padding, gridCols, gridRows } = gridDimensions;
    return {
      gridToPixel: (tileX: number, tileY: number) => ({
        x: padding + tileX * tileSize,
        y: padding + tileY * tileSize,
      }),
      pixelToGrid: (x: number, y: number) => ({
        tileX: Math.round((x - padding) / tileSize),
        tileY: Math.round((y - padding) / tileSize),
      }),
      clampToGrid: (tileX: number, tileY: number) => ({
        tileX: Math.max(0, Math.min(gridCols - 1, tileX)),
        tileY: Math.max(0, Math.min(gridRows - 1, tileY)),
      }),
    };
  }, [gridDimensions]);

  const allItemsMap = getAllItemsMap();
  const normalizedRecipes = getNormalizedRecipesMap();

  const screenPosition =
    station.gridPosition.tileX >= 0 && station.gridPosition.tileY >= 0
      ? gridToPixel(station.gridPosition.tileX, station.gridPosition.tileY)
      : { x: 0, y: 0 };

  const handleDragStart = useCallback((event, info) => {
    setIsDragging(true);
    hasMoved.current = false;
    dragStartPos.current = { x: info.point.x, y: info.point.y };
    triggerHapticFeedback('light');
    setTouchFeedback(true);
    setTimeout(() => setTouchFeedback(false), CONSTANTS.TOUCH_INTERACTION.TOUCH_FEEDBACK_DURATION);
  }, []);

  const handleDrag = useCallback((event, info) => {
    const distance = Math.sqrt(
      Math.pow(info.point.x - dragStartPos.current.x, 2) +
      Math.pow(info.point.y - dragStartPos.current.y, 2)
    );
    if (distance > CONSTANTS.TOUCH_INTERACTION.DRAG_THRESHOLD) {
      hasMoved.current = true;
    }
  }, []);

  const handleDragEnd = useCallback((event, info) => {
    setIsDragging(false);
    if (hasMoved.current) {
      const dropX = info.offset.x + screenPosition.x;
      const dropY = info.offset.y + screenPosition.y;
      const gridPos = pixelToGrid(dropX, dropY);
      const clampedGridPos = clampToGrid(gridPos.tileX, gridPos.tileY);
      dispatch({
        type: "UPDATE_BREW_STATION_GRID_POSITION",
        stationIndex,
        gridPosition: clampedGridPos,
      });
      triggerHapticFeedback('medium');
    }
  }, [screenPosition, stationIndex, dispatch, pixelToGrid, clampToGrid]);

  const handleBrewWithFeedback = useCallback(() => {
    triggerHapticFeedback('medium');
    setTouchFeedback(true);
    setTimeout(() => setTouchFeedback(false), CONSTANTS.TOUCH_INTERACTION.TOUCH_FEEDBACK_DURATION);
    handleAttemptBrew(stationIndex);
  }, [handleAttemptBrew, stationIndex]);

  const handleQuantityChangeWithFeedback = useCallback((newQuantity: number) => {
    triggerHapticFeedback('light');
    handleQuantityChange(stationIndex, newQuantity);
  }, [handleQuantityChange, stationIndex]);

  const handleRemoveWithFeedback = useCallback((slotIndex: number) => {
    triggerHapticFeedback('light');
    handleRemoveSlotItem(stationIndex, slotIndex);
  }, [handleRemoveSlotItem, stationIndex]);

  if (station.gridPosition.tileX === -1) {
    return null;
  }

  // Calculate dynamic dimensions based on grid and screen size
  const widthTiles = isLargeScreen ? CONSTANTS.BREW_STATION_GRID.DESKTOP_WIDTH_TILES : CONSTANTS.BREW_STATION_GRID.WIDTH_TILES;
  const heightTiles = isLargeScreen ? CONSTANTS.BREW_STATION_GRID.DESKTOP_HEIGHT_TILES : CONSTANTS.BREW_STATION_GRID.HEIGHT_TILES;
  
  const stationWidth = widthTiles * gridDimensions.tileSize;
  const stationHeight = heightTiles * gridDimensions.tileSize;
  
  // Calculate proportional sizing for internal elements
  const containerPadding = Math.max(CONSTANTS.BREW_STATION_GRID.MIN_PADDING, stationWidth * CONSTANTS.BREW_STATION_GRID.PADDING_RATIO);
  const elementGap = Math.max(CONSTANTS.BREW_STATION_GRID.MIN_GAP, stationWidth * CONSTANTS.BREW_STATION_GRID.GAP_RATIO);
  const titleSize = Math.max(CONSTANTS.BREW_STATION_GRID.MIN_TITLE_SIZE, stationWidth * CONSTANTS.BREW_STATION_GRID.TITLE_SIZE_RATIO);

  return (
    <motion.div
      data-tutorial-id={`brew-station-${stationIndex}`}
      className={`brew-station bg-surface/80 backdrop-blur-sm rounded-lg shadow-lg flex flex-col absolute border border-border-color select-none mobile-brew-station ${
        isDragging ? 'cursor-grabbing shadow-2xl' : 'cursor-grab'
      } ${touchFeedback ? 'ring-2 ring-primary-action/50' : ''}`}
      style={{
        width: `${stationWidth}px`,
        height: `${stationHeight}px`,
        padding: `${containerPadding}px`,
      }}
      animate={{
        x: screenPosition.x,
        y: screenPosition.y,
        scale: isDragging ? 1.05 : 1,
      }}
      initial={{
        x: screenPosition.x,
        y: screenPosition.y,
      }}
      drag
      dragConstraints={constraintsRef}
      dragMomentum={false}
      dragElastic={0.1}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      whileDrag={{
        cursor: "grabbing",
        zIndex: 10,
        scale: 1.05,
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
      }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 30,
      }}
    >
      {/* Station title - responsive to container size */}
      <h3 
        className="brew-station-title font-bold text-center text-text-primary"
        style={{
          fontSize: `${titleSize}px`,
          marginBottom: `${elementGap}px`,
          lineHeight: 1.2,
        }}
      >
        <span className="block sm:hidden">S{stationIndex + 1}</span>
        <span className="hidden sm:block">Brew Station {stationIndex + 1}</span>
      </h3>

      {/* Temperature Control - responsive to container */}
      <div style={{ marginBottom: `${elementGap}px` }}>
        <TemperatureControl
          enabled={state.temperatureControl.enabled}
          currentTemp={state.temperatureControl.currentTemp}
          onTempChange={handleTempChange}
          recipeId={
            station.slots[0] && station.slots[1]
              ? normalizedRecipes.get(
                  [station.slots[0], station.slots[1]].sort().join("_")
                )?.recipe.result.id
              : undefined
          }
          containerWidth={stationWidth}
          containerPadding={containerPadding}
        />
      </div>

      {/* Environmental Display - responsive to container */}
      <div style={{ marginBottom: `${elementGap}px` }}>
        <EnvironmentalDisplay
          enabled={state.environmentalState.enabled}
          timeOfDay={state.environmentalState.currentTimeOfDay}
          humidity={state.environmentalState.humidity}
          containerWidth={stationWidth}
          containerPadding={containerPadding}
        />
      </div>

      {/* Quality Grade Display - responsive to container */}
      <div style={{ marginBottom: `${elementGap}px` }}>
        <QualityGradeDisplay
          enabled={state.qualityGrades.enabled}
          grade={state.lastQualityGrade || undefined}
          containerWidth={stationWidth}
          containerPadding={containerPadding}
        />
      </div>

      {/* Brew Slots - responsive to container */}
      <BrewSlots
        slots={station.slots}
        onRemove={handleRemoveWithFeedback}
        allItemsMap={allItemsMap}
        containerWidth={stationWidth}
        containerPadding={containerPadding}
        elementGap={elementGap}
        isLargeScreen={isLargeScreen}
      />

      {/* Action row with Quantity and Brew button */}
      <div className="flex items-center mt-auto" style={{ gap: `${elementGap}px`, paddingTop: `${elementGap}px` }}>
        {station.slots[0] && (
          <QuantityControl
            quantity={station.quantity}
            onQuantityChange={handleQuantityChangeWithFeedback}
            containerWidth={stationWidth}
            containerPadding={containerPadding}
            elementGap={elementGap}
          />
        )}
        <BrewButton
          stationIndex={stationIndex}
          onBrew={handleBrewWithFeedback}
          disabled={!station.slots[0] || !station.slots[1]}
          touchFeedback={touchFeedback}
          containerWidth={stationWidth}
          containerPadding={containerPadding}
          elementGap={elementGap}
        />
      </div>
    </motion.div>
  );
};

export default BrewStation;
